import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Home,
  Users, 
  UserCheck, 
  Settings, 
  BarChart3, 
  Building2,
  Heart,
  Activity,
  Database,
  FileText,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Shield,
  HelpCircle,
  Mail,
  Phone,
  Globe
} from 'lucide-react';

const TenantAdminSidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      color: 'text-[#008080]',
      path: '/tenant-admin/dashboard'
    },
    
    {
      id: 'users',
      label: 'User Management',
      icon: Users,
      color: 'text-[#00BCD4]',
      path: '/tenant-admin/users'
    },
    {
      id: 'patients',
      label: 'Patient Management',
      icon: UserCheck,
      color: 'text-[#5B7DAE]',
      path: '/tenant-admin/patients'
    },
    {
      id: 'questions',
      label: 'Prompt Management',
      icon: HelpCircle,
      color: 'text-[#008B8B]',
      path: '/tenant-admin/questions'
    },
    {
      id: 'analytics',
      label: 'Analytics & Reports',
      icon: BarChart3,
      color: 'text-[#008080]',
      path: '/tenant-admin/analytics'
    },
    {
      id: 'settings',
      label: 'Organization Settings',
      icon: Settings,
      color: 'text-[#008B8B]',
      path: '/tenant-admin/settings'
    },
    {
      id: 'profile',
      label: 'Organization Profile',
      icon: Building2,
      color: 'text-[#00BCD4]',
      path: '/tenant-admin/profile'
    }
  ];

  const handleNavigation = (item) => {
    setActiveView(item.id);
    navigate(item.path);
  };

  const getCurrentPath = () => {
    return location.pathname.split('/').pop();
  };

  return (
    <div className="w-80 bg-white shadow-lg h-screen overflow-y-auto flex flex-col border-r border-[#E6EEF4]">
      {/* Logo and Brand */}
      <div className="p-6 border-b border-[#E6EEF4]">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg border border-[#E6EEF4]">
              <img 
                src="/Vital-logo.png" 
                alt="Vital Audio Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#008080] rounded-full border-2 border-white">
              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0B1C2C]">Sonometrik</h1>
            {/* <p className="text-sm text-[#3D3D3D] font-medium">Organization Admin</p> */}
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-[#008080] rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'TA'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0B1C2C] truncate">
                {user?.name || 'Tenant Administrator'}
              </p>
              <p className="text-xs text-[#3D3D3D]">Organization Admin</p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-[#008B8B] rounded-full mr-2"></div>
                <span className="text-xs text-[#008B8B] font-medium">Organization Access</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-4 space-y-1">
        <div className="mb-4">
          <p className="text-xs font-semibold text-[#5B7DAE] uppercase tracking-wide mb-3">
            ORGANIZATION MANAGEMENT
          </p>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getCurrentPath() === item.id || (getCurrentPath() === '' && item.id === 'dashboard');
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#E6EEF4] text-[#008B8B] border border-[#008B8B] shadow-sm' 
                    : 'text-[#3D3D3D] hover:text-[#0B1C2C] hover:bg-[#F9FBFC]'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-[#008B8B]' : item.color} transition-colors`} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-[#008B8B] rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Organization Health Status */}
      {/* <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Organization Health</h3>
            <Activity className="h-4 w-4 text-[#008B8B]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">User Accounts</span>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3 text-[#008B8B]" />
                <span className="text-xs font-medium text-[#008B8B]">Active</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Patient Records</span>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3 text-[#008B8B]" />
                <span className="text-xs font-medium text-[#008B8B]">Synced</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Compliance</span>
              <div className="flex items-center space-x-1">
                <AlertTriangle className="h-3 w-3 text-[#00BCD4]" />
                <span className="text-xs font-medium text-[#00BCD4]">Review</span>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Quick Stats */}
      {/* <div className="p-4">
        <div className="bg-[#F9FBFC] p-4 rounded-xl border border-[#E6EEF4]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0B1C2C]">Quick Stats</h3>
            <TrendingUp className="h-4 w-4 text-[#008080]" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Staff Members</span>
              <span className="text-sm font-bold text-[#008080]">24</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">Active Patients</span>
              <span className="text-sm font-bold text-[#008B8B]">156</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#3D3D3D]">This Month</span>
              <span className="text-sm font-bold text-[#00BCD4]">+18%</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Footer */}
      <div className="p-4 border-t border-[#E6EEF4]">
        <div className="text-center">
          <p className="text-xs text-[#5B7DAE]">
            Sonometrik v1.0.0
          </p>
          <p className="text-xs text-[#5B7DAE]">
            © 2025 Healthcare Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantAdminSidebar;